import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
    primaryText,
    secondaryText,
    greyTextColor,
    borderPrimary,
    placeholderPrimary,
    greenColorClass,
    cancelColorClass,
} from "../../../../../styling/stylingHelper";
import { eur } from "../../../../orderhistory/utils";
import { PRACTICE_PLATFORM, buildPracticeProducts } from "../../../../onboarding/practice/costPriceFixture";
import { armPracticeSubrecipeCreate, disarmPracticeSubrecipeCreate } from "../../../../onboarding/practice/practiceGuards";
import { scopedAnchor, usePracticeScope } from "../../../../onboarding/practice/practiceScope";
import { useSubrecipeDetail, SubrecipeSeedIngredient } from "./useSubrecipeDetail";
import type { Subrecipe } from "../../../../../services/subrecipes/subrecipesService";
import AddIngredients from "./AddIngredients";
import AlternativesView from "./AlternativesView";
import LinkCustomIngredientModal from "./LinkCustomIngredientModal";
import { productKey } from "../../../../../utils/reports/shared";
import SubrecipeDetailView from "./SubrecipeDetailView";
import SubrecipeDraftIngredients from "./SubrecipeDraftIngredients";
import DeleteSubrecipeEntityModal from "../../modals/DeleteSubrecipeEntityModal";

export interface SubrecipeDetailBodyProps {
    // null → create mode (empty draft); a string → edit that subrecipe.
    subrecipeId: string | null;
    onClose: () => void;
    // Called with the saved subrecipe after a create/update, and with nothing after
    // a delete — hosts that only refresh a list can ignore the argument.
    onSaved?: (saved?: Subrecipe) => void;
    // Chrome selection. "slideOver" (the SubrecipeDetailModal wrapper) renders the
    // portal + backdrop + sliding panel with the maximize animation; "embedded"
    // (default) renders the editor inline for hosts that supply their own frame.
    // All editor behavior is identical across both.
    host?: "slideOver" | "embedded";
    // Optional strip rendered full-width directly below the header row (above the
    // scroll area) — the product-setup flow passes its stepper here. Main view only.
    subHeader?: React.ReactNode;
    // On mount, scroll to the order-history ingredient row whose resolver key
    // (productKey) matches this and focus its quantity input.
    initialFocusProductKey?: string;
    // Row the draft starts with — see `useSubrecipeDetail`'s seedIngredient.
    seedIngredient?: SubrecipeSeedIngredient;
    allowEmptyIngredients?: boolean;
    seedName?: string;
    restaurantId?: string;
    // Whether a successful save closes the panel (default true). Hosts that keep
    // the editor mounted pass false and navigate from `onSaved`.
    closeOnSave?: boolean;
    // When set, the mobile header arrow and the footer's Cancel return to the host's
    // previous view instead of closing. Used when the editor is one step of a flow.
    onBack?: () => void;
    /**
     * Layer of the `host="slideOver"` chrome. The portal drops this panel into the root
     * stacking context, so the default z-30 lands BELOW any host above z-30 — pass
     * `overlayTopLayer` when opening this from inside another panel. Ignored when embedded.
     */
    zIndexClass?: string;
}

// Base field styling WITHOUT a width, so callers set their own (the portions row
// is a flex layout — a `w-full` here would make the unit <select> claim the whole
// row and collapse the number input to its padding).
const fieldInputBase = `rounded-lg border bg-backgroundSecondary dark:bg-backgroundSecondary-dark px-3 h-11 text-[14px] leading-5 ${secondaryText} ${placeholderPrimary} outline-none border-borderGrey dark:border-borderGrey-dark`;
const fieldInputClass = `w-full ${fieldInputBase}`;
const fieldLabelClass = `block mb-2 text-[14px] leading-5 font-medium ${primaryText}`;

/**
 * The full subrecipe editor experience — the draft form plus the view-swap ladder
 * (AddIngredients / AlternativesView / nested SubrecipeDetailView). Rendered inside
 * the slide-over by SubrecipeDetailModal (`host="slideOver"`), or inline by other
 * hosts (`host="embedded"`, default) such as the product-setup stepper.
 */
const SubrecipeDetailBody: React.FC<SubrecipeDetailBodyProps> = ({
    subrecipeId,
    onClose,
    onSaved,
    host = "embedded",
    subHeader,
    initialFocusProductKey,
    seedIngredient,
    allowEmptyIngredients = false,
    seedName,
    restaurantId,
    closeOnSave = true,
    onBack,
    zIndexClass = "z-30",
}) => {
    const { t } = useTranslation();
    const detail = useSubrecipeDetail(subrecipeId, onClose, onSaved, { seedIngredient, closeOnSave, allowEmptyIngredients, seedName, restaurantId });
    const reloadSubrecipes = detail.reloadSubrecipes;

    /* Guided tour: fill the form, open/close the nested picker, and save, for a user
       who presses Next instead of doing it by hand. `cost-ensure-subrecipe-ingredient`
       is answered here rather than by driving the picker, because only this component
       can see whether the draft already holds the row. */
    // Inherited from the cost-price drawer above: true only for the tour's own editor.
    const practiceScope = usePracticeScope();

    /* A create request carries no id, so `practiceCreateSubrecipe` cannot recognise it the
       way it recognises every other write. THIS form being open is the fact that stands in
       for one, so the flag is armed for exactly as long as the practice create form is
       mounted, and never by a real one. */
    useEffect(() => {
        if (!practiceScope || subrecipeId) return;
        armPracticeSubrecipeCreate();
        return () => disarmPracticeSubrecipeCreate();
    }, [practiceScope, subrecipeId]);

    const onboardingRef = useRef(detail);
    onboardingRef.current = detail;
    useEffect(() => {
        if (!practiceScope) return;
        const onName = (event: Event) => {
            const name = (event as CustomEvent<{ name?: string }>).detail?.name;
            if (name) onboardingRef.current.setName(name);
        };
        const onPortions = (event: Event) => {
            const { value, unit } = (event as CustomEvent<{ value?: string; unit?: string }>).detail ?? {};
            if (value) onboardingRef.current.handlePortionsChange(value);
            if (unit) onboardingRef.current.setPortionsUnit(unit);
        };
        const onOpenAdd = () => onboardingRef.current.setShowAddIngredients(true);
        const onCloseAdd = () => onboardingRef.current.setShowAddIngredients(false);
        const onSave = () => { void onboardingRef.current.handleSave(); };
        const onEnsureIngredient = (event: Event) => {
            const { name, quantity } = (event as CustomEvent<{ name?: string; quantity?: string }>).detail ?? {};
            if (!name) return;
            const wanted = name.trim().toLocaleLowerCase();
            const already = onboardingRef.current.ingredients.some(
                (ingredient: any) => (ingredient.productName ?? "").toLocaleLowerCase() === wanted
            );
            if (already) return;
            const product = buildPracticeProducts().find(entry => entry.name.toLocaleLowerCase() === wanted);
            if (!product) return;
            onboardingRef.current.handleAddSelection({
                kind: "product",
                product: { ...product, platform: PRACTICE_PLATFORM },
                quantityNeeded: parseFloat(quantity ?? "") || 100,
            });
        };
        window.addEventListener("onboarding:cost-set-subrecipe-name", onName);
        window.addEventListener("onboarding:cost-set-subrecipe-portions", onPortions);
        window.addEventListener("onboarding:cost-subrecipe-open-add", onOpenAdd);
        window.addEventListener("onboarding:cost-subrecipe-close-add", onCloseAdd);
        window.addEventListener("onboarding:cost-save-subrecipe", onSave);
        window.addEventListener("onboarding:cost-ensure-subrecipe-ingredient", onEnsureIngredient);
        return () => {
            window.removeEventListener("onboarding:cost-set-subrecipe-name", onName);
            window.removeEventListener("onboarding:cost-set-subrecipe-portions", onPortions);
            window.removeEventListener("onboarding:cost-subrecipe-open-add", onOpenAdd);
            window.removeEventListener("onboarding:cost-subrecipe-close-add", onCloseAdd);
            window.removeEventListener("onboarding:cost-save-subrecipe", onSave);
            window.removeEventListener("onboarding:cost-ensure-subrecipe-ingredient", onEnsureIngredient);
        };
    }, [practiceScope]);

    const title = detail.isEditMode ? t("costPrice.editSubrecipe.editSubrecipe") : t("costPrice.createSubrecipe.title");

    // Deleting is a management action, not a linking one: the embedded host is a
    // step in another flow, and DeleteSubrecipeEntityModal is a plain `fixed` overlay
    // that a transformed host panel would confine. Slide-over only.
    const canDelete = host === "slideOver" && detail.isEditMode;

    // The draft row whose Alternatives editor is open (view-swap), resolved from
    // the enriched normal rows so it carries its current `alternatives` array.
    const alternativesIngredient = detail.alternativesIngredientId
        ? detail.enrichedNormalIngredients.find((i: any) => i.id === detail.alternativesIngredientId)
        : undefined;

    const content = detail.showAddIngredients ? (
        <AddIngredients
            mode="subrecipe"
            restaurantId={detail.restaurantId!}
            dataOverride={detail.sourceData}
            onBack={() => detail.setShowAddIngredients(false)}
            onClose={detail.handleClose}
            onAddIngredient={detail.handleAddSelection}
            excludeSubrecipeIds={detail.excludedSubrecipeIds}
            defaultWastePercentage={detail.defaultWastePercentage}
        />
    ) : alternativesIngredient ? (
        <AlternativesView
            primary={alternativesIngredient}
            requestContext={!practiceScope ? { restaurantId: detail.restaurantId, targetLabel: detail.name, source: subrecipeId ? { kind: 'subrecipe', id: subrecipeId } : undefined } : undefined}
            requestDisabled={!subrecipeId || detail.hasUnsavedChanges || detail.isSaving}
            alternatives={alternativesIngredient.alternatives ?? []}
            restaurantId={detail.restaurantId!}
            dataOverride={detail.sourceData}
            defaultWastePercentage={detail.defaultWastePercentage}
            onChange={(next) => detail.handleSaveAlternatives(alternativesIngredient.id, next)}
            onBack={() => detail.setAlternativesIngredientId(null)}
            onClose={detail.handleClose}
        />
    ) : detail.selectedNestedSubrecipeId ? (
        <SubrecipeDetailView
            subrecipeId={detail.selectedNestedSubrecipeId}
            dataOverride={detail.sourceData}
            onBack={() => detail.setSelectedNestedSubrecipeId(null)}
            onClose={detail.handleClose}
        />
    ) : (
        <>
            {/* Desktop header: maximize (left), delete [edit only] + close (right) */}
            <div className="hidden md:flex items-center justify-between px-5 py-4 border-b border-[#E8E8E9] dark:border-borderPrimary-dark">
                <button
                    type="button"
                    className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#E8E8E9] dark:border-borderPrimary-dark"
                    onClick={() => detail.setIsMaximized(prev => !prev)}
                >
                    <img src="/assets/maximize.svg" alt="expand" />
                </button>
                <div className="flex items-center gap-2">
                    {canDelete && (
                        <button
                            type="button"
                            onClick={() => detail.setShowDeleteModal(true)}
                            className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#E8E8E9] dark:border-borderPrimary-dark hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                            title={t("costPrice.subrecipes.deleteEntity.title")}
                        >
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2.5 5H4.16667H17.5" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M6.66699 5V3.33333C6.66699 2.89131 6.84259 2.46738 7.15515 2.15482C7.46771 1.84226 7.89164 1.66667 8.33366 1.66667H11.667C12.109 1.66667 12.5329 1.84226 12.8455 2.15482C13.158 2.46738 13.3337 2.89131 13.3337 3.33333V5M15.8337 5V16.6667C15.8337 17.1087 15.6581 17.5326 15.3455 17.8452C15.033 18.1577 14.609 18.3333 14.167 18.3333H5.83366C5.39164 18.3333 4.96771 18.1577 4.65515 17.8452C4.34259 17.5326 4.16699 17.1087 4.16699 16.6667V5H15.8337Z" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M8.33301 9.16667V14.1667" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M11.667 9.16667V14.1667" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={detail.handleClose}
                        className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#E8E8E9] dark:border-borderPrimary-dark"
                    >
                        <img src="/assets/close-modal.svg" alt="close" />
                    </button>
                </div>
            </div>

            {/* Host-supplied strip (e.g. the setup flow's stepper), full-width directly
                below the header row and above the scroll area. */}
            {subHeader && <div className="flex-shrink-0">{subHeader}</div>}

            {detail.isLoading ? (
                <div className="flex-1 flex items-center justify-center">
                    <p className={`${greyTextColor} text-[14px]`}>{t("costPrice.editSubrecipe.loadingSubrecipe")}</p>
                </div>
            ) : (
                <>
                    <div className="flex-1 overflow-y-auto px-4 sm:px-5 pb-5 pt-5 md:pt-4">
                        {/* Mobile header */}
                        <div className="flex items-center gap-4 md:hidden mb-5">
                            <button type="button" onClick={onBack ?? detail.handleClose}>
                                <img src="/assets/arrow-narrow-left.svg" alt="arrow-narrow-left" />
                            </button>
                            <span className="text-[24px] leading-9 font-semibold tracking-[-0.24px] text-[#181624] dark:text-textPrimary-dark">
                                {title}
                            </span>
                        </div>

                        <h2 className="hidden md:block text-[28px] leading-10 tracking-[-0.28px] font-bold text-[#161924] dark:text-textPrimary-dark mb-6">
                            {title}
                        </h2>

                        {detail.error && (
                            <div className="mb-4 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-200 text-[14px]">
                                {detail.error}
                            </div>
                        )}

                        {/* Name */}
                        <div className="mb-4" data-onboarding={scopedAnchor(practiceScope, "cost-subrecipe-name")}>
                            <label className={fieldLabelClass}>{t("costPrice.createSubrecipe.name")}</label>
                            <input
                                type="text"
                                value={detail.name}
                                onChange={(e) => detail.setName(e.target.value)}
                                placeholder={t("costPrice.createSubrecipe.namePlaceholder")}
                                className={fieldInputClass}
                            />
                        </div>

                        {/* Description */}
                        <div className="mb-4">
                            <label className={fieldLabelClass}>{t("costPrice.createSubrecipe.description")}</label>
                            <input
                                type="text"
                                value={detail.description}
                                onChange={(e) => detail.setDescription(e.target.value)}
                                placeholder={t("costPrice.createSubrecipe.descriptionPlaceholder")}
                                className={fieldInputClass}
                            />
                        </div>

                        {/* Portions + unit */}
                        <div className="mb-2" data-onboarding={scopedAnchor(practiceScope, "cost-subrecipe-portions")}>
                            <label className={fieldLabelClass}>{t("costPrice.editSubrecipe.portionsThisRecipeMakes")}</label>
                            <div className="flex items-center gap-3">
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={detail.portionsInputValue}
                                    onChange={(e) => detail.handlePortionsChange(e.target.value)}
                                    className={`${fieldInputBase} flex-1 min-w-0`}
                                />
                                <select
                                    value={detail.portionsUnit}
                                    onChange={(e) => detail.setPortionsUnit(e.target.value)}
                                    className={`${fieldInputBase} w-32 flex-shrink-0`}
                                >
                                    <option value="p">{t("costPrice.editSubrecipe.pieces")}</option>
                                    <option value="g">{t("costPrice.editSubrecipe.grams")}</option>
                                    <option value="ml">{t("costPrice.editSubrecipe.milliliters")}</option>
                                </select>
                            </div>
                        </div>

                        {/* Ingredient list */}
                        {detail.ingredients.length === 0 ? (
                            <p className={`${greyTextColor} text-[14px] leading-[21px] mt-6 mb-2 text-center`}>
                                {t("costPrice.editSubrecipe.selectIngredientsPrompt")}
                            </p>
                        ) : (
                            <SubrecipeDraftIngredients
                                requestContext={!practiceScope ? { restaurantId: detail.restaurantId, targetLabel: detail.name, source: subrecipeId ? { kind: 'subrecipe', id: subrecipeId } : undefined } : undefined}
                                requestDisabled={!subrecipeId || detail.hasUnsavedChanges || detail.isSaving}
                                normalIngredients={detail.enrichedNormalIngredients}
                                nestedRows={detail.nestedRows}
                                inlineQuantities={detail.inlineQuantities}
                                inlineWaste={detail.inlineWaste}
                                confirmingDeleteId={detail.confirmingDeleteId}
                                defaultWastePercentage={detail.defaultWastePercentage}
                                onQuantityChange={detail.handleQuantityChange}
                                onSaveEditQuantity={detail.handleSaveEditQuantity}
                                onWasteChange={detail.handleWasteChange}
                                onSaveWaste={detail.handleSaveWaste}
                                onDeleteRow={detail.handleDeleteRow}
                                onConfirmDeleteRow={detail.handleConfirmDeleteRow}
                                onNestedQuantityChange={detail.handleNestedQuantityChange}
                                onOpenNested={detail.setSelectedNestedSubrecipeId}
                                onOpenAlternatives={(ing) => detail.setAlternativesIngredientId(ing.id)}
                                onLinkCustomIngredient={(ing) => detail.setCustomLinkIngredientId(ing.id)}
                                focusProductKey={initialFocusProductKey}
                            />
                        )}

                        <button
                            type="button"
                            data-onboarding={scopedAnchor(practiceScope, "cost-subrecipe-add-ingredient")}
                            className={`mt-6 flex w-full items-center justify-center h-11 text-[16px] leading-6 font-semibold gap-2 ${greenColorClass} rounded-lg`}
                            onClick={() => detail.setShowAddIngredients(true)}
                        >
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10 4.16699V15.8337" stroke="white" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M4.1665 10H15.8332" stroke="white" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            {t("costPrice.createSubrecipe.addIngredients")}
                        </button>
                    </div>

                    {/* Footer: live cost + actions */}
                    <div className={`border-t bg-backgroundSecondary dark:bg-backgroundSecondary-dark ${borderPrimary}`}>
                        <div className="flex items-center justify-between gap-4 px-4 sm:px-5 pt-4">
                            <div className="flex flex-col">
                                <span className={`text-[11px] leading-3 uppercase tracking-wide ${greyTextColor}`}>
                                    {t("costPrice.subrecipeDetail.totalCost")}
                                </span>
                                <span className={`mt-1 text-[15px] leading-5 font-semibold ${primaryText}`}>
                                    {eur(detail.totalCost)}
                                </span>
                            </div>
                            <div className="flex flex-col items-end">
                                <span className={`text-[11px] leading-3 uppercase tracking-wide ${greyTextColor}`}>
                                    {t("costPrice.subrecipeDetail.costPerPortion")}
                                </span>
                                <span className={`mt-1 text-[15px] leading-5 font-semibold ${primaryText}`}>
                                    {eur(detail.unitCost)}
                                </span>
                            </div>
                        </div>
                        <div className="flex items-center justify-between gap-4 sm:gap-6 px-4 sm:px-5 py-4">
                            <button
                                type="button"
                                onClick={onBack ?? detail.handleClose}
                                className={`flex-1 h-11 justify-center text-[14px] leading-5 font-semibold ${cancelColorClass}`}
                            >
                                {onBack ? t("productSetup.back") : t("common.cancel")}
                            </button>
                            <button
                                type="button"
                                data-onboarding={scopedAnchor(practiceScope, "cost-subrecipe-save")}
                                onClick={detail.handleSave}
                                disabled={!detail.canSave}
                                className={`flex-1 h-11 justify-center text-[16px] leading-6 font-semibold gap-2 ${greenColorClass} disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                                {detail.isSaving
                                    ? (detail.isEditMode ? t("costPrice.editSubrecipe.updating") : t("costPrice.createSubrecipe.creating"))
                                    : (detail.isEditMode ? t("costPrice.editSubrecipe.updateSubrecipe") : t("costPrice.createSubrecipe.create"))}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </>
    );

    // The custom row whose link picker is open, resolved from the draft rows.
    const customLinkIngredient = detail.customLinkIngredientId
        ? detail.enrichedNormalIngredients.find((i: any) => i.id === detail.customLinkIngredientId)
        : undefined;

    // Rendered OUTSIDE the transformed panel (like deleteModal): a `transform`
    // ancestor makes `position: fixed` resolve against the 584px panel instead of
    // the viewport, which would confine the slide-over.
    const linkModal = customLinkIngredient ? (
        <LinkCustomIngredientModal
            ingredient={customLinkIngredient}
            dataOverride={detail.sourceData}
            itemId={detail.subrecipeId ?? 'draft'}
            existingRows={detail.enrichedNormalIngredients
                .filter((row: any) => row.id !== customLinkIngredient.id && !row.isCustom && !!row.productName)
                .map((row: any) => ({
                    key: productKey(row.platform ?? '', row.articleId, row.productName),
                    quantityNeeded: row.quantityNeeded,
                    stdUnit: row.quantityInfo?.stdUnit,
                }))}
            defaultWastePercentage={detail.defaultWastePercentage}
            onApply={(candidate, quantity) => detail.handleApplyCustomLink(customLinkIngredient.id, candidate, quantity)}
            // This editor is a local draft that only persists on Save, while fan-out targets
            // are written immediately — offering both would let Cancel leave other dishes
            // changed and this prep unchanged. The fan-out stays available from the dish side.
            offerApplyElsewhere={false}
            onClose={() => detail.setCustomLinkIngredientId(null)}
        />
    ) : null;

    const deleteModal = canDelete && detail.showDeleteModal && detail.subrecipeId ? (
        <DeleteSubrecipeEntityModal
            subrecipeId={detail.subrecipeId}
            subrecipeName={detail.name}
            onClose={() => detail.setShowDeleteModal(false)}
            onDeleted={async () => {
                detail.setShowDeleteModal(false);
                await reloadSubrecipes();
                onSaved?.();
                detail.handleClose();
            }}
        />
    ) : null;

    if (host === "slideOver") {
        // Render into document.body: an ancestor cost-price panel uses `transform`,
        // which would make `position: fixed` resolve against that 584px panel instead
        // of the viewport (confining this slide-over + letting its backdrop click
        // reach the parent). The old CreateSubrecipeModal used a portal for the same
        // reason.
        return createPortal(
            <>
                <div
                    className={`fixed inset-0 ${zIndexClass} flex justify-end bg-[#111827]/40 dark:bg-black/40 pt-[72px] md:pt-0`}
                    onClick={detail.handleClose}
                >
                    <div
                        data-onboarding={scopedAnchor(practiceScope, "cost-subrecipe-panel")}
                        className={`h-full w-full bg-white dark:bg-background-dark shadow-xl flex flex-col transform transition-all duration-250 ease-out ${detail.isPanelVisible ? "translate-x-0" : "translate-x-full"
                            } ${detail.isMaximized
                                ? "max-w-full md:max-w-[1024px]"
                                : "max-w-full md:max-w-[584px]"
                            }`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {content}
                    </div>
                </div>
                {deleteModal}
                {linkModal}
            </>,
            document.body
        );
    }

    // Embedded chrome: plain full-height column the host frames itself.
    return (
        <div className="flex h-full w-full flex-col bg-white dark:bg-background-dark">
            {content}
            {deleteModal}
            {linkModal}
        </div>
    );
};

export default SubrecipeDetailBody;
